import _ from 'lodash'
import moment from 'moment/moment'
import React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Input, Button, Label } from 'reactstrap'

import type { ExportDataType } from 'api/tenants'
import { ExportDataTypes } from 'api/tenants'

import { showError } from 'slices/notificationSlice'
import { getExportDataUrl, resetExportDataUrl, selectTenantsStatus } from 'slices/tenantsSlice'
import { getWorkspaceList, selectWorkspacesStatus } from 'slices/workspacesSlice'

import { CustomModal, SingleDateRangePicker, ItemEdit } from 'components/common'
import SelectBox from 'components/common/FormFormat/SelectBox'
import type { SuggestionItem } from 'components/common/types'
import { downloadByURL } from 'components/common/utils'

type Props = {
  open: boolean
  setOpen: (prop: boolean) => void
  currentWorkspaceId: number | undefined
}

type ExportDataItemType = { key: ExportDataType; value: string }

const DATE_PICKER_MAX_DAYS = 31

const exportDataItem: ExportDataItemType[] = [
  { key: ExportDataTypes.PlanAndRecord, value: '作業計画 + 実績' },
  { key: ExportDataTypes.PlanDetail, value: '作業計画 (詳細)' },
]

const onDetailClick = () => window.open('https://help.smileboard.jp/export-to-csv', '_blank')

const CsvExportDialog: React.FC<Props> = ({ open, setOpen, currentWorkspaceId }) => {
  const [includeProductivityValue, setIncludeProductivityValue] = React.useState(true)
  const [includeRecord, setIncludeRecord] = React.useState<boolean | undefined>(undefined)
  const [openRangeDatePicker, setOpenRangeDatePicker] = React.useState(false)
  const [selectedWorkspaces, setSelectedWorkspaces] = React.useState<SuggestionItem[]>([])
  const [period, setPeriod] = React.useState<{ start: Date; end: Date }>()
  const [exportDataType, setExportDataType] = React.useState(exportDataItem[0])
  const dispatch = useDispatch()
  const { workspaces } = useSelector(selectWorkspacesStatus, shallowEqual)
  const { downloadUrl } = useSelector(selectTenantsStatus, shallowEqual)

  React.useEffect(() => {
    dispatch(getWorkspaceList())
  }, [dispatch])

  const initExportSettings = React.useCallback(() => {
    if (!currentWorkspaceId) {
      return
    }
    const target = workspaces.find(workspace => workspace.workspaceId === currentWorkspaceId)
    if (target) {
      setSelectedWorkspaces([{ id: currentWorkspaceId, value: target.name }])
    }
    setPeriod(undefined)
    setIncludeProductivityValue(true)
    setExportDataType(exportDataItem[0])
  }, [workspaces, currentWorkspaceId])

  // ダイアログを開くたびに、設定を初期化する
  React.useEffect(() => {
    initExportSettings()
  }, [initExportSettings, open])

  const onApprove = () => {
    if (!period || _.isEmpty(selectedWorkspaces)) {
      return
    }
    const targetWorkspaces: number[] = selectedWorkspaces.map(w => (_.isNumber(w.id) ? w.id : Number(w.id)))
    dispatch(
      getExportDataUrl({
        startDate: moment(period.start).format('YYYY-MM-DD'),
        endDate: moment(period.end).format('YYYY-MM-DD'),
        targetWorkspaces,
        includeProductivityValue,
        includeRecord,
        exportDataType: exportDataType.key,
      })
    )
  }

  const downloadCsv = React.useCallback(
    async (url: string, filename: string) => {
      try {
        await downloadByURL(url, filename)
      } catch {
        dispatch(showError())
      } finally {
        setOpen(false)
        dispatch(resetExportDataUrl())
      }
    },
    [setOpen, dispatch]
  )

  React.useEffect(() => {
    if (!downloadUrl) {
      return
    }

    const filename = `archive-${moment().format('YYYY-MM-DD-HHmm')}.csv`
    downloadCsv(downloadUrl, filename)
  }, [downloadUrl, downloadCsv])

  const disabled = React.useMemo(() => !period || _.isEmpty(selectedWorkspaces), [period, selectedWorkspaces])

  const handleExportDataType = (item: ExportDataItemType) => {
    setIncludeRecord(item.key === ExportDataTypes.PlanDetail ? false : undefined)
    setExportDataType(item)
  }

  return (
    <div>
      <CustomModal
        isOpen={open}
        title="CSVエクスポート"
        onCancel={() => setOpen(false)}
        onHideNotification={() => {}}
        approveLabel="CSVエクスポート"
        onApprove={onApprove}
        approveDisabled={disabled}
        submitName="csv-export-dialog-submit"
      >
        <div className="ms-2">
          <div className="mb-4">
            <div className="my-2 d-flex justify-content-between align-items-center">
              <div className="fw-bold">エクスポートデータ選択</div>
              <Button color="link" className="text-decoration-none" onClick={onDetailClick}>
                エクスポートデータについて
              </Button>
            </div>
            <SelectBox
              id="export-data-type"
              value={exportDataType.value}
              items={exportDataItem}
              onSelect={item => handleExportDataType(item as ExportDataItemType)}
              className="w-50 mb-4"
            />
            <div className="my-2 fw-bold">ワークスペース選択</div>
            <div className="mb-2">他ワークスペースもまとめてエクスポートできます｡ワークスペースを選択してください｡</div>
            <ItemEdit
              items={workspaces.map(w => ({ id: w.workspaceId, value: w.name }))}
              selectedItems={selectedWorkspaces}
              label="ワークスペースを追加"
              itemName="ワークスペース"
              onChange={setSelectedWorkspaces}
            />
          </div>
          <div className="mb-4">
            <div className="my-2 fw-bold">エクスポート期間設定</div>
            <div className="mb-2">最大31日分エクスポートが可能です｡</div>
            <div className="d-flex">
              <Button outline onClick={() => setOpenRangeDatePicker(true)}>
                {period
                  ? `${moment(period.start).format('YYYY/MM/DD')} - ${moment(period.end).format('YYYY/MM/DD')}`
                  : '開始日と終了日を決定'}
              </Button>
              <SingleDateRangePicker
                isOpen={openRangeDatePicker}
                from={period && period.start}
                to={period && period.end}
                maxRange={DATE_PICKER_MAX_DAYS}
                onCancel={() => setOpenRangeDatePicker(false)}
                onChange={(start, end) => {
                  setPeriod({ start, end })
                  setOpenRangeDatePicker(false)
                }}
              />
            </div>
          </div>
          <div className="mb-4">
            <div className="my-2 fw-bold">その他の設定</div>
            <div className="form-check">
              <Input
                id="include-productivity-value"
                className="form-check-input"
                checked={includeProductivityValue}
                type="checkbox"
                onChange={e => setIncludeProductivityValue(e.target.checked)}
              />
              <Label className="form-check-label" for="include-productivity-value">
                計画からの予測に生産性調整を含む
              </Label>
            </div>

            {exportDataType.key === ExportDataTypes.PlanDetail && (
              <div className="form-check">
                <Input
                  id="include-record"
                  className="form-check-input"
                  checked={includeRecord}
                  type="checkbox"
                  onChange={e => setIncludeRecord(e.target.checked)}
                />
                <Label className="check-form-label" for="include-record">
                  計画された作業時間内に記録された実績を含む
                </Label>
              </div>
            )}
          </div>
        </div>
      </CustomModal>
    </div>
  )
}

export default CsvExportDialog
